import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { FormField, VisitDetail as Detail } from '@foundation/shared';
import {
  LOCATION_FLAG_LABEL_TH,
  VISIT_OUTCOME_LABEL_TH,
  VISIT_STATUS_LABEL_TH,
  VISIT_TYPE_LABEL_TH,
} from '@foundation/shared';
import { api, tokenStore } from '../lib/api';
import { formatDateTime, formatSatang } from '../lib/format';
import { ErrorAlert, Loading } from '../components/ui';
import { useAuth } from '../auth';

export default function VisitDetail() {
  const { id = '' } = useParams();
  const { allowed } = useAuth();
  const [v, setV] = useState<Detail | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setV(await api<Detail>(`/visits/${id}`));
    } catch (e) {
      setError(e);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function review(approved: boolean) {
    const note = prompt(approved ? 'บันทึกการตรวจ (ไม่บังคับ)' : 'เหตุผลที่ไม่ผ่าน');
    if (!approved && !note) return;
    try {
      await api(`/visits/${id}/review`, {
        method: 'POST',
        body: { approved, note: note ?? undefined },
      });
      setNotice(approved ? 'บันทึกผลการตรวจ: ผ่าน' : 'บันทึกผลการตรวจ: ไม่ผ่าน');
      await load();
    } catch (e) {
      setError(e);
    }
  }

  if (error && !v) return <ErrorAlert error={error} />;
  if (!v) return <Loading />;

  const flagged = v.locationFlag && v.locationFlag !== 'ok';
  const needsReview =
    v.status === 'completed' &&
    !v.reviewedAt &&
    (flagged || ['not_found', 'refused', 'worsened'].includes(v.outcome ?? ''));

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{v.beneficiaryName}</h1>
          <p>
            <span className="mono">{v.visitCode}</span> ·{' '}
            {VISIT_TYPE_LABEL_TH[v.visitType] ?? v.visitType} ·{' '}
            <span className="badge">{VISIT_STATUS_LABEL_TH[v.status] ?? v.status}</span>
          </p>
        </div>
        <Link className="btn secondary" to="/visits">
          ← กลับรายการ
        </Link>
      </div>

      <ErrorAlert error={error} />
      {notice && <div className="alert success">{notice}</div>}

      {flagged && (
        <div className="alert warn">
          <strong>ตำแหน่งเช็คอินผิดปกติ:</strong>{' '}
          {LOCATION_FLAG_LABEL_TH[v.locationFlag!] ?? v.locationFlag}
          {v.distanceFromHomeM !== null && ` · ห่างจากบ้าน ${v.distanceFromHomeM.toLocaleString('th-TH')} เมตร`}
          <br />
          <span className="small">
            ระบบไม่บล็อกการบันทึกงานเมื่อตำแหน่งไม่ตรง เพราะ GPS ในพื้นที่ห่างไกลเพี้ยนได้จริง
            และบางครั้งพบผู้รับความช่วยเหลือนอกบ้าน — ให้ตรวจเหตุผลที่เจ้าหน้าที่ระบุไว้
          </span>
        </div>
      )}

      {needsReview && allowed('visit:review') && (
        <div className="card">
          <h2>งานนี้รอการตรวจ</h2>
          <div className="btn-row">
            <button onClick={() => void review(true)}>ตรวจผ่าน</button>
            <button className="secondary" onClick={() => void review(false)}>
              ไม่ผ่าน — ต้องแก้ไข
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-2">
        <div className="card">
          <h2>การลงพื้นที่</h2>
          <dl className="detail-list">
            <dt>เจ้าหน้าที่</dt>
            <dd>{v.staffName}</dd>
            <dt>นัดหมาย</dt>
            <dd>{formatDateTime(v.scheduledAt)}</dd>
            <dt>เช็คอิน</dt>
            <dd>{formatDateTime(v.checkedInAt)}</dd>
            <dt>บันทึกผล</dt>
            <dd>{formatDateTime(v.submittedAt)}</dd>
            <dt>ผลการเยี่ยม</dt>
            <dd>{v.outcome ? (VISIT_OUTCOME_LABEL_TH[v.outcome] ?? v.outcome) : '—'}</dd>
            <dt>นัดครั้งถัดไป</dt>
            <dd>{formatDateTime(v.nextVisitDueAt)}</dd>
          </dl>
        </div>

        <div className="card">
          <h2>ตำแหน่งและแบบฟอร์ม</h2>
          <dl className="detail-list">
            <dt>พิกัดเช็คอิน</dt>
            <dd>
              {v.gpsLat !== null && v.gpsLng !== null ? (
                <a
                  href={`https://www.google.com/maps?q=${v.gpsLat},${v.gpsLng}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {v.gpsLat.toFixed(5)}, {v.gpsLng.toFixed(5)}
                </a>
              ) : (
                <span className="muted">ไม่มีข้อมูล</span>
              )}
            </dd>
            <dt>ความแม่นยำ</dt>
            <dd>{v.gpsAccuracyM !== null ? `${Math.round(v.gpsAccuracyM)} เมตร` : '—'}</dd>
            <dt>ระยะจากบ้าน</dt>
            <dd>
              {v.distanceFromHomeM !== null
                ? `${v.distanceFromHomeM.toLocaleString('th-TH')} เมตร`
                : '—'}
            </dd>
            <dt>แบบฟอร์ม</dt>
            <dd>
              {v.formName ? (
                <>
                  {v.formName} <span className="muted small">v{v.formVersion}</span>
                </>
              ) : (
                '—'
              )}
            </dd>
            <dt>ตรวจโดย</dt>
            <dd>
              {v.reviewedByName ? `${v.reviewedByName} · ${formatDateTime(v.reviewedAt)}` : '—'}
            </dd>
          </dl>
        </div>
      </div>

      {v.formSchema && v.formData && (
        <div className="card">
          <h2>คำตอบในแบบสำรวจ</h2>
          <AnswersView schema={v.formSchema} data={v.formData} />
        </div>
      )}

      {v.summary && (
        <div className="card">
          <h2>สรุปโดยเจ้าหน้าที่</h2>
          <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{v.summary}</p>
          {v.nextAction && (
            <>
              <h2 style={{ marginTop: 16 }}>สิ่งที่ต้องทำต่อ</h2>
              <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{v.nextAction}</p>
            </>
          )}
        </div>
      )}

      {v.reviewNote && (
        <div className="card">
          <h2>บันทึกการตรวจสอบ</h2>
          <p style={{ whiteSpace: 'pre-wrap', margin: 0 }} className="small">
            {v.reviewNote}
          </p>
        </div>
      )}

      <div className="card">
        <h2>ภาพถ่ายหน้างาน ({v.photos.length})</h2>
        {v.photos.length === 0 ? (
          <p className="muted small" style={{ margin: 0 }}>
            ไม่มีภาพถ่าย
          </p>
        ) : (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {v.photos.map((p) => (
              <figure key={p.id} style={{ margin: 0, width: 190 }}>
                <AuthedImage src={p.url} />
                <figcaption className="muted small" style={{ marginTop: 4 }}>
                  {p.takenAt ? formatDateTime(p.takenAt) : '—'}
                  {p.lat !== null && p.lng !== null && (
                    <>
                      <br />
                      <a
                        href={`https://www.google.com/maps?q=${p.lat},${p.lng}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        ดูตำแหน่งที่ถ่าย
                      </a>
                    </>
                  )}
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/**
 * รูปถูกป้องกันด้วย token — โหลดผ่าน fetch แล้วแปลงเป็น object URL
 * (ใส่ token ใน query string ไม่ได้ เพราะจะติดไปกับ referrer และ log ของ CDN)
 */
function AuthedImage({ src }: { src: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    (async () => {
      try {
        const token = tokenStore.get();
        const res = await fetch(src, {
          headers: token ? { authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (failed) {
    return (
      <div
        className="muted small"
        style={{
          width: 190,
          height: 150,
          display: 'grid',
          placeItems: 'center',
          background: 'var(--c-bg)',
          borderRadius: 8,
        }}
      >
        โหลดรูปไม่สำเร็จ
      </div>
    );
  }
  if (!url) {
    return (
      <div
        style={{ width: 190, height: 150, background: 'var(--c-bg)', borderRadius: 8 }}
        aria-label="กำลังโหลดรูป"
      />
    );
  }
  return (
    <img
      src={url}
      alt=""
      style={{ width: 190, height: 150, objectFit: 'cover', borderRadius: 8, display: 'block' }}
    />
  );
}

/** แสดงคำตอบตาม schema เวอร์ชันที่ใช้ตอนกรอก — ฟอร์มเก่าจึงอ่านออกเสมอ */
function AnswersView({
  schema,
  data,
}: {
  schema: NonNullable<Awaited<ReturnType<typeof api<Detail>>>['formSchema']>;
  data: Record<string, unknown>;
}) {
  return (
    <>
      {schema.sections.map((section) => {
        const answered = section.fields.filter(
          (f) => f.type !== 'note' && data[f.key] !== undefined && data[f.key] !== null,
        );
        if (answered.length === 0) return null;

        return (
          <div key={section.key} style={{ marginBottom: 18 }}>
            <h3 style={{ fontSize: 14, color: 'var(--c-muted)', margin: '0 0 8px' }}>
              {section.title}
            </h3>
            <dl className="detail-list">
              {answered.map((f) => (
                <div key={f.key} style={{ display: 'contents' }}>
                  <dt>{f.label}</dt>
                  <dd>{renderValue(f, data[f.key])}</dd>
                </div>
              ))}
            </dl>
          </div>
        );
      })}
    </>
  );
}

function renderValue(field: FormField, value: unknown): React.ReactNode {
  if (value === null || value === undefined) return '—';

  switch (field.type) {
    case 'boolean':
      return value ? 'ใช่' : 'ไม่ใช่';

    case 'currency':
      return formatSatang(Number(value));

    case 'select': {
      const opt = field.options?.find((o) => o.value === String(value));
      return opt?.label ?? String(value);
    }

    case 'multiselect': {
      const values = Array.isArray(value) ? value.map(String) : [String(value)];
      return values
        .map((v) => field.options?.find((o) => o.value === v)?.label ?? v)
        .join(', ');
    }

    case 'photo':
      return `${Array.isArray(value) ? value.length : 0} รูป (ดูด้านล่าง)`;

    case 'gps': {
      const p = value as { lat: number; lng: number };
      return (
        <a href={`https://www.google.com/maps?q=${p.lat},${p.lng}`} target="_blank" rel="noreferrer">
          {p.lat.toFixed(5)}, {p.lng.toFixed(5)}
        </a>
      );
    }

    case 'repeater': {
      const rows = Array.isArray(value) ? (value as Array<Record<string, unknown>>) : [];
      if (rows.length === 0) return '—';
      return (
        <ul style={{ margin: 0, paddingInlineStart: 18 }}>
          {rows.map((row, i) => (
            <li key={i}>
              {(field.fields ?? [])
                .map((child) => {
                  const v = row[child.key];
                  if (v === undefined || v === null || v === '') return null;
                  const shown = child.type === 'boolean' ? (v ? 'ใช่' : 'ไม่ใช่') : String(v);
                  return `${child.label}: ${shown}`;
                })
                .filter(Boolean)
                .join(' · ')}
            </li>
          ))}
        </ul>
      );
    }

    default:
      return <span style={{ whiteSpace: 'pre-wrap' }}>{String(value)}</span>;
  }
}
